import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, buttonVariants } from "../../components/ui/button";
import BackIcon from "../../components/back-icon";
import { ArrowRight, CircleUserRound, Loader2 } from "lucide-react";
import { useDoctor } from "../../lib/hooks/doctors/use-doctor";
import { useBookAppointment } from "../../lib/hooks/doctors/use-book-appointment";
import { useAppointmentData } from "../../contexts/appointment-data-context";
import { format } from "date-fns";
import {
  BookAppointmentSchema,
  bookAppointmentSchema,
} from "../../lib/validation/book-appointment";
import { useToast } from "../../components/ui/use-toast";
import { getBase64FromFile, getDateFromSlotTime } from "../../lib/utils";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../../components/ui/drawer";
import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/auth-context";
import { PrescriptionIcon } from "./prescriptions";
import { usePatientsByMobile } from "../../lib/hooks/doctors/use-patients-by-mobile";
import LargeConfirmAppointment from "./large-confirm-appointment";
import UpperNav from "../../components/upper-nav";

function useIsLargeDevice() {
  const [isLargeDevice, setIsLargeDevice] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    function handleResize() {
      setIsLargeDevice(window.innerWidth >= 1024);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return isLargeDevice;
}

function ConfirmAppointment() {
  const navigate = useNavigate();

  const isLargeDevice = useIsLargeDevice();


  const { toast } = useToast();
  const [confirmDrawerOpen, setConfirmDrawerOpen] = useState(false);

  const params = useParams();
  const professionalId = params.professionalId;
  const { doctor, isLoading } = useDoctor(professionalId);

  const { fetched: authFetched, mobile } = useAuth();

  const { data, setData } = useAppointmentData();

  const { patients, isFetched } = usePatientsByMobile({ enabled: true });

  const { bookAppointment, isPending } = useBookAppointment();

  async function handleBookAppointment() {
    const recordsWithBase64 = await Promise.all(
      (data?.healthRecords ?? []).map(async (record) => {
        return {
          reportData: await getBase64FromFile(record),
          reportName: record.name,
        };
      }),
    );

    const [startSlot, endSlot] = data?.slotTiming?.split(" - ") ?? ["", ""];

    const startTime = getDateFromSlotTime(data?.slotDate, startSlot);
    const endTime = getDateFromSlotTime(data?.slotDate, endSlot);

    const payload: BookAppointmentSchema = {
      patientId: data?.patient?.id ?? 0,
      appointmentType: "WALK_IN",
      appointmentExists: false,
      appointmentDetails: {
        doctorName: doctor?.fullName ?? "",
        appointmentDate: data?.slotDate ?? new Date(),
        startTime,
        endTime,
        fee: doctor?.fee == null ? undefined : doctor.fee.toString(),
        professionalId: doctor?.professionalId.toString() ?? "",
        branchId: Number(data?.branchDetails?.[0].branchId) ?? 0,
        // branchId: data?.branchDetails?.[0].branchId ?? 6,
      },

      symptoms: data?.symptoms ?? [],

      healthRecords: [
        {
          doctorNameOrLabName: data?.healthRecordPhysician,
          uploadRecords: recordsWithBase64.map((record) => ({
            reportData: record.reportData,
            reportName: record.reportName,
          })),
        },
      ],
    };

    console.log("Payload", payload)


    const parsedData = bookAppointmentSchema.safeParse(payload);

    if (parsedData.error)
      return toast({
        title: "Invalid data",
        description: parsedData.error.errors
          .map((err) => `${err.message} in ${err.path.join(".")}`)
          .join(", "),
        variant: "destructive",
      });

    if (parsedData.success) {
      bookAppointment(parsedData.data, {
        onSuccess: (newData) => {
          setConfirmDrawerOpen(false);
          document.body.style.position = "";
          setData(null);
          navigate(`/appointment/${newData?.bookingId}`);
        },
      });
    }
  }



  useEffect(() => {
    if (isFetched && !patients?.length && professionalId)
      navigate(
        `/add-patient?redirectUrl=/doctors/${professionalId}/appointment/confirm?backUrl=/doctors/${professionalId}/appointment`,
      );
  }, [patients, isFetched, professionalId, navigate]);

  useEffect(() => {
    if (!mobile && authFetched)
      navigate(
        `/verify-patient?redirect=/doctors/${professionalId}/appointment/confirm`,
      );

    if (
      isFetched &&
      patients?.length &&
      !isLoading &&
      !data?.symptoms?.length &&
      !data?.patient?.id
    )
      navigate(`/doctors/${doctor?.professionalId}/appointment`);
  }, [
    data,
    doctor?.professionalId,
    navigate,
    isLoading,
    mobile,
    authFetched,
    professionalId,
    isFetched,
    patients?.length,
  ]);

  const branch = data?.branchDetails?.[0];
  // console.log("branch : ", branch);
  // Truncate file name for display (max 20 chars for mobile)
  function truncateFileName(fileName: string, maxLength: number = 20): string {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    return `${nameWithoutExt.substring(0, maxLength - 3 - (extension?.length || 0))}...${extension}`;
  }

  return (
    <>
      <UpperNav />
      <div className="h-dvh overflow-hidden pb-16 mt-14 sm:mt-16 lg:mt-20">
        <div className="h-full overflow-y-auto pt-1">
          <div className="container">
            <div className="mb-4 flex items-center gap-2 sm:mb-8">
              <Link
                to={`/doctors/${doctor?.professionalId}/appointment`}
                className={buttonVariants({
                  variant: "ghost",
                  size: "icon",
                })}
              >
                <BackIcon />
              </Link>
              <h1 className="text-sm font-bold">Book an Appointment</h1>
            </div>

            <div>
              <div className="flex gap-3">
                {doctor?.profilePhoto ? (
                  <img
                    src={`${doctor?.profilePhoto}`}
                    alt={doctor?.fullName}
                    className="size-16 rounded-full object-contain sm:size-24"
                  />
                ) : (
                  <div className="flex size-16 shrink-0 items-center justify-center rounded-full bg-muted sm:size-20">
                    <CircleUserRound
                      className="size-full"
                      strokeWidth={1}
                    />
                  </div>
                )}
                <div className="w-full space-y-4">
                  <div>
                    <h3 className="text-sm font-bold sm:text-xl">
                      Dr {doctor?.fullName}
                    </h3>
                    {doctor?.specialities?.length && (
                      <p className="mb-1 text-xs text-muted-foreground sm:text-base">
                        {doctor?.specialities.join(", ")}
                      </p>
                    )}
                    <p className="mb-1 text-xs font-bold text-muted-foreground sm:text-base">
                      {doctor?.subDistrictOrCity}
                    </p>

                    <p className="mb-2 text-sm font-bold sm:text-lg">
                      Fee: ₹ {doctor?.fee ? doctor?.fee : "NA"}
                    </p>
                  </div>
                </div>
              </div>

              {data?.slotDate && data.dayOfWeek && data.slotTiming && (
                <>
                  <div className="mx-auto mb-6 mt-7 h-0.5 bg-border" />
                  <p className="text-sm font-bold text-primary max-sm:text-center sm:text-lg">
                  {data.slotTiming},{" " }{data.dayOfWeek},{" "} {format(data.slotDate, "d MMM yyyy")}
                  </p>
                </>
              )}
              <div className="mx-auto mb-6 mt-7 h-0.5 bg-border" />
              <h2 className="mb-1 text-left font-bold sm:text-xl">
                For {data?.patient?.name}
              </h2>
              <p className="text-sm">
                {data?.symptoms?.join(", ") ?? "No symptoms provided"}
              </p>
              <div className="mx-auto mb-6 mt-7 h-0.5 bg-border" />
              <h2 className="mb-1 text-left font-bold sm:text-xl">
                At {branch?.clinicName}
              </h2>
              {(branch?.addressLine1 || branch?.subDistrict) && (
                <p className="text-sm">
                  {[branch?.addressLine1, branch?.subDistrict].filter(Boolean).join(", ")}
                </p>
              )}

              {(branch?.state || branch?.pincode) && (
                <p className="text-sm">
                  {[branch?.state].filter(Boolean).join("")}
                  {branch?.pincode ? ` - ${branch.pincode}` : ""}
                </p>
              )}

              {!data?.healthRecords?.length ? (
                <Link
                  to={`/health-records/add?redirectUrl=/doctors/${doctor?.professionalId}/appointment/confirm`}
                  className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-primary underline"
                >
                  <span>Add health records</span>
                  <ArrowRight className="size-4" strokeWidth={3} />
                </Link>
              ) : (
                <div className="mt-4 flex w-fit gap-4 rounded-md border p-4">
                  <div className="shrink-0">
                    <PrescriptionIcon />
                  </div>
                  <div>
                    <h4 className="font-bold">
                      {data.healthRecords.map((record) => truncateFileName(record.name)).join(", ")}
                    </h4>
                    {data?.healthRecordPhysician && (
                      <p className="text-sm sm:text-base">
                        Consulted with {data.healthRecordPhysician}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center bg-white p-4">
              <Drawer open={confirmDrawerOpen} onOpenChange={setConfirmDrawerOpen}>
                <DrawerTrigger asChild>
                  <Button className="w-full max-w-sm font-bold shadow-xl">
                    Book appointment
                  </Button>
                </DrawerTrigger>
                <DrawerContent>
                  <DrawerHeader>
                    <DrawerTitle className="text-center">
                      Service and Liability Waiver Agreement
                    </DrawerTitle>
                  </DrawerHeader>
                  <div className="p-6 pt-2">
                    <p>
                      I hereby confirm, acknowledge and consent that SwingBell
                      Labs, the developer of Asimith EHR, is only a facilitator of
                      the service being provided by the doctors, and there is no
                      guarantee to the quality, delivery, or efficacy of the
                      service being provided by the doctors. I permanently
                      disclaim and hold SwingBell Labs harmless from any future
                      claims/demands against them in respect of the services
                      provided by the doctors.
                    </p>
                  </div>
                  <DrawerFooter>
                    <Button
                      className="mx-auto w-full font-bold shadow-xl sm:max-w-sm"
                      onClick={handleBookAppointment}
                      disabled={isPending}
                    >
                      {isPending && (
                        <Loader2
                          className="mr-2 size-4 animate-spin"
                          strokeWidth={3}
                        />
                      )}
                      I agree, proceed
                    </Button>
                  </DrawerFooter>
                </DrawerContent>
              </Drawer>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default ConfirmAppointment;